import React, { useEffect, useMemo, useRef, useState } from 'react';
import { baixarDaUrl, noApp } from '../helpers/appNativo';

// Player de áudio estilo WhatsApp (usado no 1:1 e nos grupos).
export function AudioBubble({ url, out }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duracao, setDuracao] = useState(0);
  const [tempoAtual, setTempoAtual] = useState(0);
  const [velocidade, setVelocidade] = useState(1); // 1x -> 1.5x -> 2x (acelerador)
  const [erroAudio, setErroAudio] = useState(false); // fonte não carregou (evita crash)

  const BARRAS = 28;
  const alturas = useMemo(() => {
    const seed = (url || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    let s = seed || 42;
    return Array.from({ length: BARRAS }, () => {
      s = (s * 1664525 + 1013904223) & 0x7fffffff;
      return 4 + (s % 13);
    });
  }, [url]);

  function fmtTempo(s) {
    if (!s || Number.isNaN(s) || !Number.isFinite(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  }

  function togglePlay() {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); return; }
    // play() rejeita (NotSupportedError) se a fonte não carregou — captura pra
    // não estourar erro não-tratado (overlay vermelho). Vira um aviso discreto.
    const p = el.play();
    if (p && typeof p.catch === 'function') p.catch(() => setErroAudio(true));
  }

  function seekByClick(e) {
    const el = audioRef.current;
    if (!el || !duracao) return;
    const rect = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - rect.left) / rect.width) * duracao;
  }

  // Acelerador de áudio (WhatsApp): cicla 1x -> 1.5x -> 2x.
  function ciclarVelocidade() {
    setVelocidade((v) => (v === 1 ? 1.5 : v === 1.5 ? 2 : 1));
  }
  // Aplica a velocidade no elemento (persiste entre play/pause; reaplica ao trocar).
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = velocidade;
  }, [velocidade]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => { setPlaying(false); setProgress(0); setTempoAtual(0); };
    const onMeta = () => setDuracao(el.duration || 0);
    const onErro = () => setErroAudio(true);
    const onTime = () => {
      setTempoAtual(el.currentTime);
      if (el.duration) setProgress((el.currentTime / el.duration) * 100);
    };
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('error', onErro);
    el.addEventListener('timeupdate', onTime);
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('error', onErro);
      el.removeEventListener('timeupdate', onTime);
    };
  }, []);

  const corBotao = out ? '#a8e6cf' : '#6bcfb5';
  const progressoIdx = Math.floor((progress / 100) * BARRAS);
  // A bolha do cliente (in) é clara: branco em cima de branco some. Segue o
  // mesmo padrão do resto do chat — claro sobre o verde, escuro sobre o branco.
  const barraCheia = out ? 'rgba(255,255,255,0.85)' : '#3D8B37';
  const barraVazia = out ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)';
  // Mostra o multiplicador enquanto toca/pausado no meio — e também quando está
  // acelerado, pra não sumir a configuração de vista.
  const mostrarVelocidade = playing || progress > 0 || velocidade !== 1;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: 240, userSelect: 'none' }}>
      <audio ref={audioRef} src={url} preload="metadata" style={{ display: 'none' }} />
      <button
        type="button"
        onClick={togglePlay}
        style={{
          width: 38, height: 38, borderRadius: '50%',
          background: corBotao, border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, color: '#1a4a3a', fontSize: '1.1rem',
        }}
      >
        <i className={`bi ${playing ? 'bi-pause-fill' : 'bi-play-fill'}`} style={{ marginLeft: playing ? 0 : 2 }} />
      </button>
      <div style={{ flex: 1 }}>
        <div
          role="slider"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
          style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 20, cursor: 'pointer' }}
          onClick={seekByClick}
          onKeyDown={(e) => {
            const el = audioRef.current;
            if (!el) return;
            if (e.key === 'ArrowRight') el.currentTime = Math.min(el.currentTime + 5, el.duration);
            if (e.key === 'ArrowLeft') el.currentTime = Math.max(el.currentTime - 5, 0);
          }}
        >
          {alturas.map((h, i) => (
            <div key={i} style={{
              flex: 1, height: h, borderRadius: 2,
              background: i < progressoIdx ? barraCheia : barraVazia,
              transition: 'background 0.08s',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
          {erroAudio ? (
            <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>
              <i className="bi bi-exclamation-triangle me-1" />áudio indisponível
            </span>
          ) : (
            <>
              <i className="bi bi-mic-fill" style={{ fontSize: '0.65rem', opacity: 0.55 }} />
              <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                {playing || progress > 0 ? fmtTempo(tempoAtual) : fmtTempo(duracao)}
              </span>
            </>
          )}
        </div>
      </div>
      {mostrarVelocidade && (
        <button
          type="button"
          onClick={ciclarVelocidade}
          title="Velocidade de reprodução (1x → 1,5x → 2x)"
          style={{
            flexShrink: 0, minWidth: 34, padding: '3px 7px', borderRadius: 11,
            cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700,
            background: out ? 'rgba(255,255,255,0.28)' : 'rgba(61, 139, 55, 0.14)',
            color: out ? '#fff' : '#2E6A29',
            border: out ? 'none' : '1px solid rgba(61, 139, 55, 0.45)',
          }}
        >
          {velocidade}x
        </button>
      )}
    </div>
  );
}

// Renderiza mídia (imagem/figurinha/áudio/vídeo/documento) — usado no 1:1 e grupos.
// Imagem/figurinha chamam onAbrirImagem(url) pra ampliar na lightbox.
export function MidiaRenderer({ mensagem, onAbrirImagem, out }) {
  const ehUrlWhatsApp = (u) => /(?:whatsapp\.net|wa\.me)/i.test(u || '');
  const url = mensagem.midia_signed_url
    || (mensagem.midia_url && /^https?:\/\//i.test(mensagem.midia_url) && !ehUrlWhatsApp(mensagem.midia_url)
      ? mensagem.midia_url
      : null);
  const tipo = mensagem.tipo;

  if (!url) {
    const labelMap = { imagem: 'imagem', audio: 'áudio', video: 'vídeo', documento: 'documento', figurinha: 'figurinha' };
    return (
      <div style={{ fontStyle: 'italic', opacity: 0.85, fontSize: '0.85rem' }}>
        <i className="bi bi-exclamation-circle me-1" />
        {labelMap[tipo] || 'mídia'} indisponível
      </div>
    );
  }

  if (tipo === 'imagem') {
    return (
      <button type="button" onClick={() => onAbrirImagem?.(url)} title="Clique para ampliar"
        style={{ padding: 0, border: 'none', background: 'transparent', display: 'block', cursor: 'zoom-in' }}>
        <img src={url} alt="imagem" style={{ maxWidth: 280, maxHeight: 280, borderRadius: 8, display: 'block' }} />
      </button>
    );
  }
  if (tipo === 'figurinha') {
    return (
      <button type="button" onClick={() => onAbrirImagem?.(url)} title="Figurinha (clique para ampliar)"
        style={{ padding: 0, border: 'none', background: 'transparent', display: 'block', cursor: 'zoom-in' }}>
        <img src={url} alt="figurinha" style={{ maxWidth: 140, maxHeight: 140, display: 'block' }} />
      </button>
    );
  }
  if (tipo === 'audio') return <AudioBubble url={url} out={out} />;
  if (tipo === 'video') {
    return (
      <video controls src={url} style={{ maxWidth: 280, maxHeight: 280, borderRadius: 8 }}>
        seu navegador não suporta vídeo
      </video>
    );
  }
  // NOME do arquivo, nunca a legenda. Eram o mesmo campo até a migração 085, e
  // aí o texto digitado junto do PDF virava o "título" do arquivo — e aparecia
  // de novo embaixo, porque a legenda é renderizada fora daqui. PDF já vem com
  // nome próprio; quem envia não escolhe o título.
  return <DocumentoBubble url={url} nome={mensagem.midia_nome} mime={mensagem.midia_mime} out={out} onAbrir={onAbrirImagem} />;
}

// Documento recebido. O caso real que motivou isto: chega um boleto em PDF e a
// atendente precisa IMPRIMIR. Antes só existia "Baixar documento" — que nem
// baixava (a rota manda application/pdf sem Content-Disposition, então o
// navegador abria numa aba), e ela tinha que salvar, achar o arquivo e imprimir
// por fora. Agora dá pra imprimir direto da conversa.
function DocumentoBubble({ url, nome, mime, out, onAbrir }) {
  const [ocupado, setOcupado] = useState('');
  const ehPdf = /pdf/i.test(mime || '') || /\.pdf($|\?)/i.test(nome || '') || /\.pdf($|\?)/i.test(url || '');
  const nomeArquivo = String(nome || '').trim() || (ehPdf ? 'documento.pdf' : 'documento');

  // No app do Fio (WebView) nada disso abaixo funciona: `<a download>` em blob:
  // não faz nada, `print()` não existe e o Android não desenha PDF dentro da
  // página. Em vez de três botões que não fazem nada, UM que entrega o arquivo
  // ao celular — e é de lá que se salva, compartilha ou imprime.
  // Quem baixa é o lado nativo, direto da URL assinada: um documento grande não
  // cabe na ponte virado em texto.
  const ehApp = noApp();
  const entregarAoSistema = async () => {
    setOcupado('abrir');
    try { await baixarDaUrl(nomeArquivo, url); } catch (e) { /* a casca já avisa */ }
    finally { setOcupado(''); }
  };

  // O front e a API ficam em domínios diferentes, então o atributo `download`
  // do <a> é ignorado e o `print()` de um iframe cross-origin é bloqueado.
  // Baixar como blob resolve os dois: o blob é da mesma origem.
  const comBlob = async (acao, rotulo) => {
    setOcupado(rotulo);
    try {
      const r = await fetch(url);
      if (!r.ok) throw new Error('falhou');
      const blob = await r.blob();
      await acao(URL.createObjectURL(blob));
    } catch (e) {
      window.open(url, '_blank', 'noopener'); // fallback: abre e o usuário resolve por lá
    } finally {
      setOcupado('');
    }
  };

  const baixar = () => comBlob(async (href) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = nomeArquivo;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 4000);
  }, 'baixar');

  const imprimir = () => comBlob(async (href) => {
    // iframe escondido com o PDF: o visualizador do navegador imprime sem que a
    // atendente precise baixar, abrir e procurar o botão.
    const frame = document.createElement('iframe');
    Object.assign(frame.style, {
      position: 'fixed', right: 0, bottom: 0, width: 0, height: 0, border: 0, visibility: 'hidden',
    });
    frame.src = href;
    frame.onload = () => {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
      } catch (e) {
        window.open(href, '_blank', 'noopener'); // navegador que não deixa: abre pra imprimir de lá
      }
      setTimeout(() => { frame.remove(); URL.revokeObjectURL(href); }, 60000);
    };
    document.body.appendChild(frame);
  }, 'imprimir');

  const botao = {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 9px', borderRadius: 6,
    border: `1px solid ${out ? 'rgba(255,255,255,0.25)' : 'var(--portal-border)'}`,
    background: out ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
    color: out ? '#fff' : 'var(--portal-text)', fontSize: '0.8rem', cursor: 'pointer',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', opacity: 0.95 }}>
        <i className={`bi ${ehPdf ? 'bi-file-earmark-pdf' : 'bi-file-earmark'}`} />
        <span style={{ wordBreak: 'break-word' }}>{nomeArquivo}</span>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {ehApp ? (
          <button type="button" onClick={entregarAoSistema} disabled={Boolean(ocupado)} style={botao}
            title="Abre no visualizador do celular — de lá dá pra salvar, imprimir ou compartilhar">
            <i className="bi bi-box-arrow-up-right" />
            {ocupado === 'abrir' ? 'Abrindo…' : 'Abrir'}
          </button>
        ) : (
        <>
        {ehPdf && (
          <button type="button" onClick={imprimir} disabled={Boolean(ocupado)} style={botao}
            title="Imprimir sem precisar baixar">
            <i className="bi bi-printer" />
            {ocupado === 'imprimir' ? 'Abrindo…' : 'Imprimir'}
          </button>
        )}
        <button type="button" onClick={baixar} disabled={Boolean(ocupado)} style={botao} title="Salvar no computador">
          <i className="bi bi-download" />
          {ocupado === 'baixar' ? 'Baixando…' : 'Baixar'}
        </button>
        {/* Abre no MESMO visualizador da foto — vê, imprime ou baixa sem sair
            da conversa. Sem o callback (uso fora da tela de conversa), abre aba. */}
        {ehPdf && onAbrir ? (
          <button
            type="button"
            onClick={() => onAbrir({ url, tipo: 'pdf', nome: nomeArquivo })}
            style={botao}
            title="Ver o documento aqui mesmo"
          >
            <i className="bi bi-eye" />
            Abrir
          </button>
        ) : (
          <a href={url} target="_blank" rel="noreferrer" style={{ ...botao, textDecoration: 'none' }} title="Abrir numa aba">
            <i className="bi bi-box-arrow-up-right" />
            Abrir
          </a>
        )}
        </>
        )}
      </div>
    </div>
  );
}
